
import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MockAPI } from '../services/api';
import { Asset, OMTask, Document } from '../types';
import { Card, Button, Badge } from '../components/ui';
import { Truck, Calendar, DollarSign, FileText, Download, Edit, Wrench, AlertTriangle } from 'lucide-react';
import NotesFeed from '../components/NotesFeed';
import ContextPhotos from '../components/ContextPhotos';
import {useAuth} from '../services/authContext';

const AssetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const {user,privileges}=useAuth();
  const scope=JSON.stringify([id,user?.email,user?.status,user?.farm_ids,privileges]);
  const current=useRef(scope);current.current=scope;
  const [loadedScope,setLoadedScope]=useState(''),[error,setError]=useState('');
  const [asset, setAsset] = useState<Asset | undefined>();
  const [maintenance, setMaintenance] = useState<OMTask[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);

  useEffect(() => {
    const load = async () => {
      if (id) {
          try{
           const [detail,tasks,docs]=await Promise.all([MockAPI.getAsset(id),privileges.projects?.view?MockAPI.getTasksForAsset(id):Promise.resolve([]),privileges.documents?.view?MockAPI.getDocumentsForEntity('asset',id,false):Promise.resolve([])]);
           if(current.current!==scope)return;
           setAsset(detail);setMaintenance(tasks);setDocuments(docs);setLoadedScope(scope);setError(detail?'':'Asset not found.');
          }catch(e){if(current.current===scope)setError((e as Error).message);}
      }
    };
    load();
  }, [scope]);

  if (!asset||loadedScope!==scope) return <div className="p-8">{error||'Loading...'}</div>;

  const isWarrantyActive = asset.warranty_expiration 
    ? new Date(asset.warranty_expiration) > new Date() 
    : false;

  return (
    <div className="space-y-6">
       <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
           <div className="flex items-center space-x-2 text-sm text-gray-500 mb-1">
             <Link to="/assets" className="hover:text-usda-700">Assets</Link>
             <span>/</span>
             <span className="text-gray-900">{asset.name}</span>
           </div>
           <div className="flex items-center space-x-3">
             <h1 className="text-3xl font-bold text-gray-900">{asset.name}</h1>
             <Badge variant={asset.status === 'Active' ? 'success' : 'neutral'}>{asset.status}</Badge>
           </div>
           <p className="text-gray-500 mt-1">{asset.brand} {asset.model_year ? `• ${asset.model_year}` : ''}</p>
        </div>
        {privileges.assets?.write&&<div className="mt-4 md:mt-0">
           <Link to={`/assets/${asset.asset_id}/edit`}>
             <Button variant="outline"><Edit className="h-4 w-4 mr-2" /> Edit Asset</Button>
           </Link>
        </div>}
      </div>

      <ContextPhotos type="asset" id={asset.asset_id} entityLabel={asset.name}/>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-6">
              <Card className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Specs & Purchase Info</h3>
                  <dl className="space-y-3 text-sm">
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                          <dt className="text-gray-500">Serial Number</dt>
                          <dd className="font-medium text-gray-900 font-mono">{asset.serial_number || 'N/A'}</dd>
                      </div>
                       <div className="flex justify-between border-b border-gray-100 pb-2">
                          <dt className="text-gray-500">Purchase Date</dt>
                          <dd className="font-medium text-gray-900">{asset.purchase_date || 'N/A'}</dd>
                      </div>
                       <div className="flex justify-between border-b border-gray-100 pb-2">
                          <dt className="text-gray-500">Condition</dt>
                          <dd className="font-medium text-gray-900">{asset.condition_at_purchase}</dd>
                      </div>
                       <div className="flex justify-between pt-1">
                          <dt className="text-gray-500 flex items-center"><DollarSign className="h-3 w-3 mr-1" /> Cost</dt>
                          <dd className="font-medium text-gray-900">{asset.purchase_price ? `$${asset.purchase_price.toLocaleString()}` : '-'}</dd>
                      </div>
                  </dl>
                  
                  {asset.description && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Description</h4>
                          <p className="text-sm text-gray-600">{asset.description}</p>
                      </div>
                  )}
              </Card>

              {/* Replace simple notes with full NotesFeed */}
              <NotesFeed entityType="asset" entityId={asset.asset_id} />

              <Card className="p-6">
                  <div className="flex justify-between items-center mb-4">
                     <h3 className="font-semibold text-gray-900">Documents</h3>
                  </div>
                   {documents.length > 0 ? (
                    <ul className="space-y-3">
                        {documents.map(doc => (
                        <li key={doc.document_id} className="flex items-center justify-between text-sm group">
                            <div className="flex items-center overflow-hidden">
                            <FileText className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                            <div className="overflow-hidden">
                                <span className="truncate block text-gray-700 font-medium">{doc.title}</span>
                                <span className="text-xs text-gray-400">{doc.notes}</span>
                            </div>
                            </div>
                            <button className="text-gray-400 hover:text-gray-600 p-1">
                            <Download className="h-4 w-4" />
                            </button>
                        </li>
                        ))}
                    </ul>
                    ) : (
                    <p className="text-xs text-gray-500 italic">No receipts or manuals attached.</p>
                    )}
              </Card>

              {asset.warranty_expiration && (
                  <Card className={`p-4 border-l-4 ${isWarrantyActive ? 'border-l-green-500' : 'border-l-red-500'}`}>
                      <div className="flex items-start">
                          <div className="flex-shrink-0">
                              {isWarrantyActive ? (
                                  <Calendar className="h-5 w-5 text-green-500" />
                              ) : (
                                  <AlertTriangle className="h-5 w-5 text-red-500" />
                              )}
                          </div>
                          <div className="ml-3">
                              <h3 className="text-sm font-medium text-gray-900">Warranty Status</h3>
                              <p className="text-sm text-gray-500 mt-1">
                                  {isWarrantyActive ? `Active until ${asset.warranty_expiration}` : `Expired on ${asset.warranty_expiration}`}
                              </p>
                          </div>
                      </div>
                  </Card>
              )}
          </div>

          <div className="lg:col-span-2">
              <Card className="h-full">
                  <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-lg">
                      <h3 className="font-semibold text-gray-900 flex items-center">
                          <Wrench className="h-4 w-4 mr-2" /> Maintenance History
                      </h3>
                      {privileges.projects?.write&&<Link to="/maintenance/new">
                        <Button variant="outline" className="text-xs py-1">Record Service</Button>
                      </Link>}
                  </div>
                  <div className="p-0">
                      {maintenance.length > 0 ? (
                          <div className="divide-y divide-gray-100">
                              {maintenance.map(task => (
                                  <div key={task.task_id} className="p-4 hover:bg-gray-50">
                                      <div className="flex justify-between items-start">
                                          <div>
                                              <div className="font-medium text-gray-900">{task.task_type}</div>
                                              <div className="text-sm text-gray-500 mt-1">{task.notes}</div>
                                          </div>
                                          <div className="text-right">
                                              {task.completed_date ? (
                                                  <Badge variant="success">Completed {task.completed_date}</Badge>
                                              ) : (
                                                  <Badge variant="warning">Scheduled {task.scheduled_date}</Badge>
                                              )}
                                              <div className="text-xs text-gray-400 mt-1">
                                                  By: {task.performed_by || 'Unknown'}
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                              ))}
                          </div>
                      ) : (
                          <div className="p-8 text-center text-gray-500">
                              No maintenance records found.
                          </div>
                      )}
                  </div>
              </Card>
          </div>
      </div>
    </div>
  );
};

export default AssetDetail;
